window.BookingHoldTimer = (() => {
  const STORAGE_KEY  = 'bta_hold';
  const TOAST_ID     = 'bta-hold-toast';
  const CART_POLL_MS = 5000;

  // ── Storage ──────────────────────────────────────────────────────────────

  function saveHold(reservationId, expiresAt, clinicVariantId, cartSnapshot) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      reservationId, expiresAt, clinicVariantId, cartSnapshot
    }));
  }

  function loadHold() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); }
    catch { return null; }
  }

  function clearHold() { sessionStorage.removeItem(STORAGE_KEY); }

  // ── Helpers ───────────────────────────────────────────────────────────────

  function formatTime(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  }

  function formatExpiry(isoString) {
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  function _isPostCheckout() {
    const path   = window.location.pathname.toLowerCase();
    const search = window.location.search.toLowerCase();
    return (
      path.includes('/thank-you') || path.includes('/thank_you') ||
      path.includes('/orders/') ||
      search.includes('order_id=') || search.includes('checkout_token=')
    );
  }

  // ── Cart helpers ──────────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
  }

  function cartHasVariant(cart, variantId) {
    if (!variantId || !cart?.items) return false;
    return cart.items.some(item => String(item.variant_id) === String(variantId));
  }

  function removeClinicItem(clinicVariantId) {
    return fetchCart().then(cart => {
      const item = (cart.items || []).find(i => String(i.variant_id) === String(clinicVariantId));
      if (!item) return;
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });
    });
  }

  // ── Snapshot ──────────────────────────────────────────────────────────────

  function snapshotCart(reservationId, expiresAt, clinicVariantId) {
    fetchCart()
      .then(cart => {
        const cartSnapshot = (cart.items || []).map(item => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {}
        }));
        saveHold(reservationId, expiresAt, clinicVariantId, cartSnapshot);
      })
      .catch(() => saveHold(reservationId, expiresAt, clinicVariantId, []));
  }

  // ── Keeping the floating buttons clear ────────────────────────────────────
  // The chat and compare tabs (assets/floating-buttons.css) sit at the bottom right, where the toast is: on
  // phones the toast is full width. While it shows, html gets bht-on and --bht-clear (how far up the screen
  // the toast reaches), and the tabs rise above it. Measured, so it also holds above the sticky bar.

  let _clearObserver = null;

  // Writes only what changed: classList.add/remove rewrite the class attribute even when the class is already
  // (or not) there, and the observer below watches that attribute, so unconditional writes would loop every frame.
  function _syncClearance() {
    const root = document.documentElement;
    const toast = document.getElementById(TOAST_ID);
    if (!toast || !toast.isConnected) {
      if (root.classList.contains('bht-on')) root.classList.remove('bht-on');
      if (root.style.getPropertyValue('--bht-clear')) root.style.removeProperty('--bht-clear');
      return;
    }
    const clear = Math.max(0, Math.round(window.innerHeight - toast.getBoundingClientRect().top)) + 'px';
    if (root.style.getPropertyValue('--bht-clear') !== clear) root.style.setProperty('--bht-clear', clear);
    if (!root.classList.contains('bht-on')) root.classList.add('bht-on');
  }

  function _watchClearance(toast) {
    if (_clearObserver) _clearObserver.disconnect();
    // after the slide-in, whenever the toast changes size (the cancel question is taller), and when the
    // sticky bar comes or goes (it moves the toast)
    toast.addEventListener('animationend', _syncClearance);
    if (window.ResizeObserver) { _clearObserver = new ResizeObserver(_syncClearance); _clearObserver.observe(toast); }
    if (window.MutationObserver && !_watchClearance.html) {
      _watchClearance.html = new MutationObserver(() => requestAnimationFrame(_syncClearance));
      _watchClearance.html.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('resize', _syncClearance);
    }
    _syncClearance();
  }

  // ── Toast ─────────────────────────────────────────────────────────────────

  function createToast(expiresAt) {
    const existing = document.getElementById(TOAST_ID);
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = TOAST_ID;
    toast.className = 'bht-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    toast.innerHTML = `
      <div class="bht-view bht-main" data-bht-view="main">
        <button class="bht-dismiss" aria-label="Dismiss">&#x2715;</button>
        <div class="bht-label">Slot reserved</div>
        <div class="bht-until">Until ${formatExpiry(expiresAt)}</div>
        <div class="bht-progress-track"><div class="bht-progress-bar"></div></div>
        <div class="bht-timer-row">
          <svg class="bht-icon" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
          </svg>
          <span class="bht-countdown">--:--</span>
        </div>
        <button class="bht-return-btn" type="button">&#8592; Return to basket</button>
      </div>
      <div class="bht-view bht-confirm" data-bht-view="confirm">
        <p class="bht-confirm-msg">Cancel your booking? Your slot will be released and the appointment removed from your basket.</p>
        <div class="bht-confirm-actions">
          <button class="bht-confirm-back" type="button">Keep booking</button>
          <button class="bht-confirm-yes"  type="button">Yes, cancel</button>
        </div>
      </div>
    `;

    _setView(toast, 'main');
    document.body.appendChild(toast);
    _watchClearance(toast);

    toast.querySelector('.bht-dismiss').addEventListener('click', () => {
      _setView(toast, 'confirm');
    });

    // Return to basket — just navigate, variant is already in cart
    toast.querySelector('.bht-return-btn').addEventListener('click', () => {
      window.location.href = '/cart';
    });

    toast.querySelector('.bht-confirm-back').addEventListener('click', () => {
      _setView(toast, 'main');
    });

    toast.querySelector('.bht-confirm-yes').addEventListener('click', () => {
      const yesBtn = toast.querySelector('.bht-confirm-yes');
      if (yesBtn) { yesBtn.disabled = true; yesBtn.textContent = 'Cancelling…'; }
      const cid = loadHold()?.clinicVariantId;
      _teardown();
      removeClinicItem(cid).catch(() => {}).finally(() => location.reload());
    });

    return toast;
  }

  function _setView(toast, viewName) {
    toast.querySelectorAll('.bht-view').forEach(el => {
      el.style.display = el.dataset.bhtView === viewName ? '' : 'none';
    });
  }

  function removeToast() {
    const t = document.getElementById(TOAST_ID);
    if (t) t.remove();
    if (_clearObserver) { _clearObserver.disconnect(); _clearObserver = null; }
    _syncClearance();
  }

  // ── Countdown ─────────────────────────────────────────────────────────────

  let _timerInterval = null;

  function startCountdown(expiresAt) {
    if (_timerInterval) clearInterval(_timerInterval);

    const expiryMs  = new Date(expiresAt).getTime();
    const totalSecs = Math.max(1, Math.round((expiryMs - Date.now()) / 1000));

    function tick() {
      const remaining = Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));
      const toast = document.getElementById(TOAST_ID);
      if (toast) {
        const cd = toast.querySelector('.bht-countdown');
        if (cd) cd.textContent = formatTime(remaining);
        const bar = toast.querySelector('.bht-progress-bar');
        if (bar) bar.style.width = `${(remaining / totalSecs) * 100}%`;
        toast.classList.toggle('bht-expiring', remaining < 180);
      }
      if (remaining <= 0) {
        const cid = loadHold()?.clinicVariantId;
        _teardown();
        removeClinicItem(cid).catch(() => {}).finally(() => location.reload());
      }
    }

    tick();
    _timerInterval = setInterval(tick, 1000);
  }

  // ── Cart watch ────────────────────────────────────────────────────────────

  let _cartPollInterval = null;

  function startCartWatch(clinicVariantId) {
    stopCartWatch();
    _cartPollInterval = setInterval(() => {
      fetchCart()
        .then(cart => {
          // Variant gone from cart → user removed it or checked out → teardown silently
          if (!cartHasVariant(cart, clinicVariantId)) {
            _teardown();
            removeToast();
          }
        })
        .catch(() => {});
    }, CART_POLL_MS);
  }

  function stopCartWatch() {
    if (_cartPollInterval) { clearInterval(_cartPollInterval); _cartPollInterval = null; }
  }

  // ── Teardown ──────────────────────────────────────────────────────────────

  function _teardown() {
    if (_timerInterval) { clearInterval(_timerInterval); _timerInterval = null; }
    stopCartWatch();
    clearHold();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  function startHold(reservationId, expiresAt, clinicVariantId) {
    saveHold(reservationId, expiresAt, clinicVariantId, []);
    snapshotCart(reservationId, expiresAt, clinicVariantId); // async, updates snapshot in background

    createToast(expiresAt);
    startCountdown(expiresAt);
    startCartWatch(clinicVariantId);
  }

  function restoreIfActive() {
    if (_isPostCheckout()) { clearHold(); return; }

    const hold = loadHold();
    if (!hold) return;

    if (new Date(hold.expiresAt).getTime() - Date.now() <= 0) { clearHold(); return; }

    // Guard: check cart actually still has the clinic variant
    fetchCart()
      .then(cart => {
        if (!cartHasVariant(cart, hold.clinicVariantId)) {
          // Variant gone — checkout completed or user cleared cart
          clearHold();
          return;
        }
        createToast(hold.expiresAt);
        startCountdown(hold.expiresAt);
        startCartWatch(hold.clinicVariantId);
      })
      .catch(() => {
        // Network error — show toast as fallback
        createToast(hold.expiresAt);
        startCountdown(hold.expiresAt);
        startCartWatch(hold.clinicVariantId);
      });
  }

  return { startHold, restoreIfActive, clearHold };
})();

document.addEventListener('DOMContentLoaded', () => BookingHoldTimer.restoreIfActive());